import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { InquiryStatus } from '@paladin-monorepo/common';
import { DashboardWidgetCard } from '@/components/dashboard/DashboardWidgetCard';
import type { StatusSegment } from './inquirer-dashboard-data';

const STATUS_COLORS: Record<InquiryStatus, string> = {
  [InquiryStatus.Draft]: 'var(--text-disabled)',
  [InquiryStatus.InReview]: 'var(--text-subtle)',
  [InquiryStatus.Published]: 'var(--brand-secondary)',
  [InquiryStatus.Evaluated]: 'var(--success)',
  [InquiryStatus.Archived]: 'var(--border-subtle)',
};

export function InquiryStatusDonut({ segments }: { segments: StatusSegment[] }) {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      segments
        .filter((s) => s.count > 0)
        .map((s) => ({
          name: t(`inquiries.status.${s.status}`),
          value: s.count,
          status: s.status,
        })),
    [segments, t]
  );

  return (
    <DashboardWidgetCard
      className="min-h-[320px]"
      title={t('dashboard.inquirer.widgets.status.title')}
      description={t('dashboard.inquirer.widgets.status.subtitle')}
      contentClassName="h-[260px] px-2 pb-4 pt-0"
    >
      {data.length === 0 ? (
        <p className="text-muted-foreground flex h-full items-center justify-center text-sm">
          {t('dashboard.inquirer.widgets.status.empty')}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={56}
              outerRadius={88}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
            >
              {data.map((entry) => (
                <Cell key={entry.status} fill={STATUS_COLORS[entry.status]} stroke="transparent" />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => [value, t('dashboard.inquirer.widgets.status.count')]} />
            <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </DashboardWidgetCard>
  );
}
